'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import AssessmentForm from './components/AssessmentForm';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface AthleteOption {
  id: string;
  name: string;
  sport: string;
  position: string;
  heightIn: number;
  weightLbs: number;
  trainingAge: number;
  injuryHistory: string;
  seasonPhase: string;
  avatarInitials: string;
}

export default function AssessmentEntryPage() {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState<AthleteOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    async function load() {
      setLoading(true);
      // Coaches see all their athletes; athletes see only themselves
      const { data: coachAthletes } = await supabase
        .from('coach_athletes')
        .select('athlete_id')
        .eq('coach_id', user!.id)
        .eq('status', 'active');

      const coachAthleteIds = (coachAthletes ?? []).map((r: any) => r.athlete_id);

      // Also include the user's own athlete profile if they have one
      const { data: ownProfile } = await supabase
        .from('athlete_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();

      const allIds = [...new Set([
        ...coachAthleteIds,
        ...(ownProfile ? [ownProfile.id] : []),
      ])];

      if (allIds.length > 0) {
        const { data: profiles } = await supabase
          .from('athlete_profiles')
          .select('id, name, sport, position, height_in, weight_lbs, training_age, injury_history, season_phase, avatar_initials')
          .in('id', allIds);

        setAthletes((profiles ?? []).map((p: any) => ({
          id: p.id,
          name: p.name ?? '',
          sport: p.sport ?? '',
          position: p.position ?? '',
          heightIn: Number(p.height_in ?? 0),
          weightLbs: Number(p.weight_lbs ?? 0),
          trainingAge: Number(p.training_age ?? 0),
          injuryHistory: p.injury_history ?? '',
          seasonPhase: p.season_phase ?? '',
          avatarInitials: p.avatar_initials ?? (p.name ?? '??').slice(0, 2).toUpperCase(),
        })));
      }
      setLoading(false);
    }

    load();
  }, [user]);

  return (
    <AppLayout activePath="/assessment-entry">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="page-title">Assessment Entry</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Enter raw performance test data to generate an Explosive Power Profile
            </p>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="text-center space-y-3">
              <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-zinc-500">Loading athletes...</p>
            </div>
          </div>
        ) : (
          <AssessmentForm athletes={athletes} />
        )}
      </div>
    </AppLayout>
  );
}